import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ProductList = ({ 
  products = [], 
  onEdit, 
  onDelete, 
  onStatusChange, 
  onStockAdjustment 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'asc' });
  const [editingStock, setEditingStock] = useState(null);
  const [stockValue, setStockValue] = useState('');

  const categories = [...new Set(products.map(p => p.category))];

  const filteredAndSortedProducts = React.useMemo(() => {
    let filtered = products.filter(product => {
      const matchesSearch = !searchTerm || 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.brand.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
      
      const matchesStatus = statusFilter === 'all' || product.status === statusFilter;
      
      return matchesSearch && matchesCategory && matchesStatus;
    });

    // Sort
    filtered.sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];
      
      if (sortConfig.direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [products, searchTerm, categoryFilter, statusFilter, sortConfig]);

  const getStatusConfig = (product) => {
    if (product.currentStock === 0) {
      return {
        label: 'Sin Stock',
        bgColor: 'bg-error-100',
        textColor: 'text-error-700',
        icon: 'XCircle'
      };
    } else if (product.currentStock <= product.minimumStock) {
      return {
        label: 'Stock Bajo',
        bgColor: 'bg-warning-100',
        textColor: 'text-warning-700',
        icon: 'AlertTriangle'
      };
    } else {
      return {
        label: 'Normal',
        bgColor: 'bg-success-100',
        textColor: 'text-success-700',
        icon: 'CheckCircle'
      };
    }
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleStockEdit = (productId, currentStock) => {
    setEditingStock(productId);
    setStockValue(currentStock.toString());
  };

  const handleStockSave = (productId) => {
    const newStock = parseInt(stockValue);
    const product = products.find(p => p.id === productId);
    
    if (product && !isNaN(newStock) && newStock >= 0) {
      const adjustment = newStock - product.currentStock;
      if (adjustment !== 0) {
        onStockAdjustment(productId, adjustment, 'manual_adjustment', 'Ajuste manual desde lista');
      }
    }
    
    setEditingStock(null);
    setStockValue('');
  };

  const handleStockCancel = () => {
    setEditingStock(null);
    setStockValue('');
  };

  return (
    <div className="bg-surface rounded-lg border border-border">
      {/* Filters */}
      <div className="p-6 border-b border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Input
              placeholder="Buscar productos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              iconName="Search"
            />
          </div>
          
          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">Todas las categorías</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
              <option value="discontinued">Descontinuados</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-text-secondary">
              {filteredAndSortedProducts.length} productos
            </span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary-50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center space-x-1">
                  <span>Producto</span>
                  {sortConfig.field === 'name' && (
                    <Icon 
                      name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={12} 
                    />
                  )}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                onClick={() => handleSort('sku')}
              >
                <div className="flex items-center space-x-1">
                  <span>SKU</span>
                  {sortConfig.field === 'sku' && (
                    <Icon 
                      name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={12} 
                    />
                  )}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                onClick={() => handleSort('price')}
              >
                <div className="flex items-center space-x-1">
                  <span>Precio</span>
                  {sortConfig.field === 'price' && (
                    <Icon 
                      name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={12} 
                    />
                  )}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-center text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-secondary-100"
                onClick={() => handleSort('currentStock')}
              >
                <div className="flex items-center justify-center space-x-1">
                  <span>Stock</span>
                  {sortConfig.field === 'currentStock' && (
                    <Icon 
                      name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                      size={12} 
                    />
                  )}
                </div>
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-text-secondary uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-text-secondary uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredAndSortedProducts.map((product) => {
              const statusConfig = getStatusConfig(product);
              const isEditingThisStock = editingStock === product.id;

              return (
                <tr key={product.id} className="hover:bg-secondary-25">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center">
                        <Icon name="Package" size={20} className="text-text-muted" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-text-primary">
                          {product.name}
                        </div>
                        <div className="text-sm text-text-muted">
                          {product.brand} {product.model}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-text-primary font-mono">
                    {product.sku}
                  </td>
                  <td className="px-6 py-4 text-sm text-text-primary">
                    €{product.price?.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {isEditingThisStock ? (
                      <div className="flex items-center justify-center space-x-2">
                        <Input
                          type="number"
                          min="0"
                          value={stockValue}
                          onChange={(e) => setStockValue(e.target.value)}
                          className="w-20 text-center"
                        />
                        <div className="flex space-x-1">
                          <Button
                            variant="primary"
                            size="xs"
                            onClick={() => handleStockSave(product.id)}
                            iconName="Check"
                          />
                          <Button
                            variant="secondary"
                            size="xs"
                            onClick={handleStockCancel}
                            iconName="X"
                          />
                        </div>
                      </div>
                    ) : (
                      <div
                        className="cursor-pointer"
                        onClick={() => handleStockEdit(product.id, product.currentStock)}
                      >
                        <div className="text-sm font-medium text-text-primary">
                          {product.currentStock}
                        </div>
                        <div className="text-xs text-text-muted">
                          Min: {product.minimumStock}
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}
                    >
                      <Icon name={statusConfig.icon} size={12} className="mr-1" />
                      {statusConfig.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <Button
                        variant="secondary"
                        size="xs"
                        onClick={() => onEdit(product)}
                        iconName="Edit"
                      />
                      
                      <div className="relative group">
                        <Button
                          variant="secondary"
                          size="xs"
                          iconName="MoreVertical"
                        />
                        <div className="absolute right-0 top-full mt-1 w-48 bg-surface rounded-lg border border-border shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                          <div className="py-1">
                            <button
                              onClick={() => onStatusChange(product.id, 
                                product.status === 'active' ? 'inactive' : 'active'
                              )}
                              className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-secondary-50"
                            >
                              {product.status === 'active' ? 'Desactivar' : 'Activar'}
                            </button>
                            <button
                              onClick={() => onStatusChange(product.id, 'discontinued')}
                              className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-secondary-50"
                            >
                              Marcar Descontinuado
                            </button>
                            <hr className="my-1 border-border" />
                            <button
                              onClick={() => onDelete(product.id)}
                              className="w-full px-4 py-2 text-left text-sm text-error hover:bg-error-50"
                            >
                              Eliminar
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredAndSortedProducts.length === 0 && (
          <div className="p-8 text-center">
            <Icon name="Package" size={48} className="text-text-muted mx-auto mb-4" />
            <p className="text-text-muted">
              {searchTerm || categoryFilter !== 'all' || statusFilter !== 'all' ?'No se encontraron productos con los filtros aplicados' :'No hay productos registrados'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductList;